// The frame every signed-in screen sits inside: sidebar, header, content.
//
// Two rules hold this together, and both exist because the till is used on
// whatever monitor the shop happens to own, at whatever Windows display
// scaling somebody set years ago:
//
//   NOTHING SCROLLS SIDEWAYS. The frame is a fixed-height flex column with
//   `overflow-x-hidden`, and every flex child carries `min-w-0`. Without that
//   one class a flex item refuses to shrink below its content - a long product
//   name or a wide table then pushes the whole document sideways and the cart
//   ends up off the edge of the screen.
//
//   THE SIDEBAR GETS OUT OF THE WAY. Between roughly 1024 and 1400 CSS pixels
//   - which is where a 1366 or 1440 laptop lands once display scaling is
//   applied - the menu collapses itself to the icon rail so the POS keeps its
//   product grid AND its Current Order. Above that it is left alone.

import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { useI18n } from '../i18n/I18nProvider';

const COLLAPSE_KEY = 'yaghi.sidebarCollapsed';

/// Below this the sidebar is a slide-over anyway, so nothing to decide.
const RAIL_FROM = 1024;
/// Above this there is room for the full menu next to a POS in two columns.
const RAIL_UNTIL = 1400;

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t } = useI18n();

  /// What the person actually chose, remembered separately from what the
  /// screen forced. Widening the window hands them their choice back rather
  /// than leaving the menu collapsed because it once had to be.
  const chosen = useRef(collapsed);
  /// True while the width - not the person - is holding the rail closed.
  const forced = useRef(false);

  useEffect(() => {
    // Two listeners for one decision, which looks like belt and braces and is.
    //
    // `matchMedia` is the efficient one: it fires only when the boundary is
    // actually crossed. `resize` is the one that catches the cases matchMedia
    // misses - and there are some. Neither event is dispatched at all by the
    // viewport override used to check this layout at seven widths, so that
    // check was done by reloading at each size; a real browser fires both.
    //
    // Running `apply` on mount is what makes the reload path work, and it is
    // also what makes this correct on first paint rather than one frame late.
    const apply = () => {
      const inBand = window.innerWidth >= RAIL_FROM && window.innerWidth < RAIL_UNTIL;

      if (inBand) {
        forced.current = true;
        setCollapsed(true);
      } else if (forced.current) {
        forced.current = false;
        setCollapsed(chosen.current);
      }
    };

    apply();

    const media = window.matchMedia(
      `(min-width: ${RAIL_FROM}px) and (max-width: ${RAIL_UNTIL - 1}px)`,
    );
    media.addEventListener('change', apply);
    window.addEventListener('resize', apply);

    return () => {
      media.removeEventListener('change', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      // An explicit toggle is a decision, so it becomes the remembered one and
      // the screen stops overriding it until the width changes again.
      chosen.current = next;
      forced.current = false;
      try {
        localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        // Remembering the preference is a nicety, not worth an error.
      }
      return next;
    });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      {/* Lets a keyboard user jump past the menu straight to the page.
          Translated: an Arabic screen with an English skip link is the first
          thing a screen-reader user hears, and it was English. */}
      <a href="#main-content" className="sr-only-focusable">
        {t.common.skipToContent}
      </a>

      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header onOpenMobileMenu={() => setMobileOpen(true)} />

        {/* `overflow-x-hidden` is the backstop. Every screen inside is built
            not to need it, and if one ever does the page still refuses to
            scroll sideways rather than hiding the cart off the edge. */}
        <main
          id="main-content"
          className="scrollbar-brand min-w-0 flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6"
          tabIndex={-1}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
